import i18next from 'i18next';
import { toast } from 'sonner';

import { useTailorabilityStore } from '@/store/tailorability.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

const t = (k, fb) => i18next.t(k, { defaultValue: fb });
const errMsg = (e, fb) => getIntegrationErrorMessage(e) || fb;

/** Empty object sentinel: truthy so shell does not re-fetch after a silent failure. */
const BRANDING_FALLBACK = Object.freeze({});

/** Shared in-flight promise — collapses LoadingLayout→MainLayout remount + StrictMode double-invoke. */
let brandingLoadPromise = null;

export class TailorabilityIntegration {
    constructor(service) {
        this.service = service;
    }

    /**
     * Shell branding load (sidebar logo / appName). No toast; deduped; safe for remounts.
     * Admin editor continues to use loadTab('branding') which surfaces errors.
     */
    async ensureBrandingLoaded() {
        const s = useTailorabilityStore.getState();
        if (s.branding) {
            return { success: true, data: s.branding };
        }
        if (brandingLoadPromise) {
            return brandingLoadPromise;
        }

        brandingLoadPromise = (async () => {
            try {
                const r = await this.service.getBranding();
                const data = r.data || BRANDING_FALLBACK;
                useTailorabilityStore.getState().setBranding(data);
                return { success: true, data };
            } catch (e) {
                useTailorabilityStore.getState().setBranding(BRANDING_FALLBACK);
                return {
                    success: false,
                    data: BRANDING_FALLBACK,
                    error: errMsg(e, t('tailorability:editor.loadFailed', { defaultValue: 'Could not load' })),
                };
            } finally {
                brandingLoadPromise = null;
            }
        })();

        return brandingLoadPromise;
    }

    async loadTab(tab) {
        const s = useTailorabilityStore.getState();
        s.setActiveTab(tab);
        s.setLoading(true);
        s.setError(null);
        try {
            if (tab === 'custom-fields') {
                const r = await this.service.listCustomFields();
                s.setCustomFields(r.data || []);
            } else if (tab === 'form-layouts') {
                const r = await this.service.listFormLayouts();
                s.setFormLayouts(r.data || []);
            } else if (tab === 'custom-permissions') {
                const r = await this.service.listCustomPermissions();
                s.setCustomPermissions(r.data || []);
            } else if (tab === 'branding') {
                const r = await this.service.getBranding();
                s.setBranding(r.data || BRANDING_FALLBACK);
            } else if (tab === 'feature-flags') {
                const r = await this.service.listFeatureFlags();
                s.setFeatureFlags(r.data || []);
            }
            return { success: true };
        } catch (e) {
            const m = errMsg(e, t('tailorability:editor.loadFailed', { defaultValue: 'Could not load' }));
            s.setError(m);
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setLoading(false);
        }
    }

    async createCustomField(body) {
        try {
            const r = await this.service.createCustomField(body);
            toast.success(t('tailorability:messages.createSuccess', { defaultValue: 'Record created' }));
            await this.loadTab('custom-fields');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async updateCustomField(id, body) {
        try {
            const r = await this.service.updateCustomField(id, body);
            toast.success(t('tailorability:messages.updateSuccess', { defaultValue: 'Record updated' }));
            await this.loadTab('custom-fields');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotUpdate', { defaultValue: 'Could not update' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async deleteCustomField(id) {
        try {
            const r = await this.service.deleteCustomField(id);
            toast.success(t('tailorability:messages.deleteSuccess', { defaultValue: 'Record deleted' }));
            await this.loadTab('custom-fields');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, 'Silinemedi');
            toast.error(m);
            return { success: false, error: m };
        }
    }

    /** GET custom field definitions for entity forms (no success toast). */
    async fetchCustomFieldsByEntity(entityType) {
        try {
            const r = await this.service.customFieldsByEntity(entityType);
            return { success: true, data: r.data || [] };
        } catch (e) {
            return { success: false, data: [], error: errMsg(e, i18next.t('integrationToasts:couldNotLoadCustomFields', { defaultValue: 'Could not load custom fields' })) };
        }
    }

    /** GET EntityFormLayout for entity+mode (tenant override, else global). No success toast. */
    async fetchFormLayoutByEntity(entityType, mode = 'create') {
        try {
            const r = await this.service.formLayoutsByEntity(entityType, { mode });
            return { success: true, data: r.data || null };
        } catch (e) {
            return {
                success: false,
                data: null,
                error: errMsg(e, i18next.t('integrationToasts:couldNotLoadFormLayout', {
                    defaultValue: 'Could not load form layout',
                })),
            };
        }
    }

    async createFormLayout(body) {
        try {
            const r = await this.service.createFormLayout(body);
            toast.success(t('tailorability:messages.createSuccess', { defaultValue: 'Record created' }));
            await this.loadTab('form-layouts');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async updateFormLayout(id, body) {
        try {
            const r = await this.service.updateFormLayout(id, body);
            toast.success(t('tailorability:messages.updateSuccess', { defaultValue: 'Record updated' }));
            await this.loadTab('form-layouts');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotUpdate', { defaultValue: 'Could not update' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async deleteFormLayout(id) {
        try {
            const r = await this.service.deleteFormLayout(id);
            toast.success(t('tailorability:messages.deleteSuccess', { defaultValue: 'Record deleted' }));
            await this.loadTab('form-layouts');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, 'Silinemedi');
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async upsertBranding(body) {
        try {
            const r = await this.service.upsertBranding(body);
            toast.success(t('tailorability:messages.brandingSaved', { defaultValue: 'Branding saved' }));
            await this.loadTab('branding');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, 'Kaydedilemedi');
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async upsertFeatureFlag(body) {
        try {
            const r = await this.service.upsertFeatureFlag(body);
            toast.success(t('tailorability:messages.flagSaved', { defaultValue: 'Flag updated' }));
            await this.loadTab('feature-flags');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, 'Kaydedilemedi');
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async createCustomPermission(body) {
        try {
            const r = await this.service.createCustomPermission(body);
            toast.success(t('tailorability:messages.createSuccess', { defaultValue: 'Record created' }));
            await this.loadTab('custom-permissions');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async updateCustomPermission(id, body) {
        try {
            const r = await this.service.updateCustomPermission(id, body);
            toast.success(t('tailorability:messages.updateSuccess', { defaultValue: 'Record updated' }));
            await this.loadTab('custom-permissions');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotUpdate', { defaultValue: 'Could not update' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async deleteCustomPermission(id) {
        try {
            const r = await this.service.deleteCustomPermission(id);
            toast.success(t('tailorability:messages.deleteSuccess', { defaultValue: 'Record deleted' }));
            await this.loadTab('custom-permissions');
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, 'Silinemedi');
            toast.error(m);
            return { success: false, error: m };
        }
    }

    /** Quiet catalog load for Role UI overlay (no tab switch / success toast). */
    async fetchCustomPermissionDefsByPageKey() {
        try {
            const r = await this.service.listCustomPermissions();
            const rows = r.data || [];
            const map = {};
            for (const row of rows) {
                if (!row?.pageKey || row.isActive === false) continue;
                map[String(row.pageKey)] = {
                    pageKey: row.pageKey,
                    actions: Array.isArray(row.actions) ? row.actions : ['View'],
                    moduleKey: row.moduleKey,
                    isActive: true,
                };
            }
            return { success: true, data: map };
        } catch (e) {
            return { success: false, data: {}, error: errMsg(e, 'Could not load custom permissions') };
        }
    }

    static rowId(row) {
        return String(row?._id ?? row?.id ?? row?.key ?? row?.fieldKey ?? '');
    }

    /**
     * Notification deep-link locate across tailorability tabs (client-held lists).
     */
    async locateRowInList({ entityId } = {}) {
        if (!entityId) return { found: false };
        const target = String(entityId);
        const prevTab = useTailorabilityStore.getState().activeTab;
        const tabs = [
            { tab: 'custom-fields', get: (s) => s.customFields },
            { tab: 'form-layouts', get: (s) => s.formLayouts },
            { tab: 'custom-permissions', get: (s) => s.customPermissions },
            { tab: 'feature-flags', get: (s) => s.featureFlags },
        ];

        for (const { tab, get } of tabs) {
            let list = get(useTailorabilityStore.getState()) || [];
            if (!list.some((row) => TailorabilityIntegration.rowId(row) === target)) {
                await this.loadTab(tab);
                list = get(useTailorabilityStore.getState()) || [];
            }
            if (list.some((row) => TailorabilityIntegration.rowId(row) === target)) {
                useTailorabilityStore.getState().setActiveTab(tab);
                return { found: true };
            }
        }

        useTailorabilityStore.getState().setActiveTab(prevTab);
        return { found: false };
    }
}
